import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  MapPin, Building2, DoorOpen, Calendar as CalIcon, Clock,
  User, Phone, Users, CheckCircle2, ArrowLeft, ArrowRight, Loader2,
  Lock, ShieldCheck, LogOut, KeyRound, Sparkles, Plus, Check,
  Coffee, Cake, Gift, Tag, PartyPopper
} from "lucide-react";
import { waUrl } from "./lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_KEY = "cv_user_auth";

const Step = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2" data-testid={`step-${n}`}>
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${
      done ? "bg-[#7ad99c] text-[#0a0604] border-[#7ad99c]" :
      active ? "bg-[#f4d089] text-[#1a0f06] border-[#f4d089]" :
      "bg-transparent text-[#a89878] border-[#3a2a14]"
    }`}>{done ? "✓" : n}</div>
    <span className={`text-[11px] tracking-wider hidden sm:block ${active ? "text-[#f4d089]" : "text-[#a89878]"}`}>{label}</span>
  </div>
);

const Card = ({ children, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left rounded-lg border p-4 transition w-full ${
      active
        ? "border-[#f4d089] bg-[#1a0f06] shadow-[0_0_0_2px_rgba(244,208,137,0.25)]"
        : "border-[#3a2a14] bg-[#0d0905] hover:border-[#d4a574]/60"
    }`}
  >
    {children}
  </button>
);

const RoomImages = ({ images, alt }) => {
  const [i, setI] = useState(0);

  if (!images || images.length === 0) return null;

  const prev = (e) => {
    e.stopPropagation();
    setI((x) => (x - 1 + images.length) % images.length);
  };

  const next = (e) => {
    e.stopPropagation();
    setI((x) => (x + 1) % images.length);
  };

  return (
    <div className="relative -mx-4 -mt-4 mb-3 h-44 overflow-hidden rounded-t-lg bg-[#0a0604]">
      <img
        src={images[i]}
        alt={alt}
        className="w-full h-full object-cover"
      />

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/50 text-white w-7 h-7 rounded-full"
          >
            ‹
          </button>

          <button
            type="button"
            onClick={next}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/50 text-white w-7 h-7 rounded-full"
          >
            ›
          </button>

          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, k) => (
              <span
                key={k}
                className={`w-1.5 h-1.5 rounded-full ${
                  k === i ? "bg-[#f4d089]" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Btn = ({ children, className = "", disabled, ...p }) => (
  <button
    disabled={disabled}
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-6 py-3 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    {...p}
  >
    {children}
  </button>
);

const Input = (p) => (
  <input
    {...p}
    className={`w-full bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-[#7a6c54] focus:outline-none focus:border-[#d4a574] ${p.className || ""}`}
  />
);

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Book() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [cities, setCities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [otherBranchImages, setOtherBranchImages] = useState({}); // { [branchId]: imageUrl }
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [city, setCity] = useState(params.get("city_id") || "");
  const [branch, setBranch] = useState(params.get("branch_id") || "");
  const [room, setRoom] = useState("");
  const [date, setDate] = useState(params.get("date") || "");
  const [selectedSlots, setSelectedSlots] = useState([]); // array of consecutive slot strings
  const slot = selectedSlots[0] || ""; // first slot kept for legacy fields
  const [name, setName] = useState("");
const [phone, setPhone] = useState("");
const [email, setEmail] = useState("");
const [gstin, setGstin] = useState("");
  const [occasion, setOccasion] = useState("");
  const [specialRequest, setSpecialRequest] = useState("");
  const [guests, setGuests] = useState(Number(params.get("guests")) || 2);
  const [confirmation, setConfirmation] = useState(null);
  const [payCfg, setPayCfg] = useState({ enabled: false, key_id: "", advance_amount: 750 });
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]); // array of addon ids
  const [paymentType, setPaymentType] = useState("full"); // full | advance
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponErr, setCouponErr] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Customer auth (mobile OTP)
  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(localStorage.getItem(AUTH_KEY) || "null"); } catch { return null; }
  });
  const [authMode, setAuthMode] = useState("phone"); // phone -> otp
  const [otpPhone, setOtpPhone] = useState("");
  const [otpName, setOtpName] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpHint, setOtpHint] = useState("");
  const [authErr, setAuthErr] = useState("");

  // Password-based auth (login/signup)
  const [authMethod, setAuthMethod] = useState("password"); // password | otp
  const [pwMode, setPwMode] = useState("login"); // login | signup
  const [pwPhone, setPwPhone] = useState("");
  const [pwPassword, setPwPassword] = useState("");
  const [pwName, setPwName] = useState("");
  const [pwEmail, setPwEmail] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const submitPassword = async () => {
    setAuthErr(""); setPwBusy(true);
    try {
      const ph = pwPhone.replace(/\D/g, "");
      if (ph.length !== 10) throw new Error("Enter a valid 10-digit mobile number");
      if (!pwPassword || pwPassword.length < 6) throw new Error("Password must be at least 6 characters");
      if (pwMode === "signup" && !pwName.trim()) throw new Error("Please enter your full name");
      const url = pwMode === "signup" ? `${API}/auth/register` : `${API}/auth/login`;
      const payload = pwMode === "signup"
        ? { phone: ph, password: pwPassword, name: pwName.trim(), email: pwEmail.trim() }
        : { phone: ph, password: pwPassword };
      const r = await axios.post(url, payload);
      const a = { token: r.data.token, user: r.data.user };
      setAuth(a);
      localStorage.setItem(AUTH_KEY, JSON.stringify(a));
      setPwPassword("");
    } catch (e) {
      setAuthErr(e.response?.data?.detail || e.message || "Could not sign in");
    } finally { setPwBusy(false); }
  };

  // Apply auth prefill into booking fields
  useEffect(() => {
    if (auth?.user) {
      if (!name && auth.user.name) setName(auth.user.name);
if (!phone && auth.user.phone) setPhone(auth.user.phone);
if (!email && auth.user.email) setEmail(auth.user.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const sendOtp = async () => {
    setAuthErr(""); setOtpHint(""); setOtpSending(true);
    try {
      const r = await axios.post(`${API}/auth/otp/send`, { phone: otpPhone });
      setAuthMode("otp");
      if (r.data?.dev_otp) setOtpHint(`Mock OTP (dev): ${r.data.dev_otp}`);
    } catch (e) {
      setAuthErr(e.response?.data?.detail || "Could not send OTP");
    } finally { setOtpSending(false); }
  };

  const verifyOtp = async () => {
    setAuthErr(""); setOtpVerifying(true);
    try {
      const r = await axios.post(`${API}/auth/otp/verify`, {
        phone: otpPhone, otp: otpCode, name: otpName, email: otpEmail,
      });
      const a = { token: r.data.token, user: r.data.user };
      setAuth(a);
      localStorage.setItem(AUTH_KEY, JSON.stringify(a));
      setOtpCode(""); setOtpHint("");
    } catch (e) {
      setAuthErr(e.response?.data?.detail || "Could not verify OTP");
    } finally { setOtpVerifying(false); }
  };

  const logout = () => {
    setAuth(null);
    localStorage.removeItem(AUTH_KEY);
    setAuthMode("phone");
    setOtpPhone(""); setOtpCode(""); setOtpName(""); setOtpEmail("");
  };

  // Pre-jump from home search: if both city and branch came in via URL, skip to step 2
  const [didPrefill, setDidPrefill] = useState(false);
  useEffect(() => {
    if (didPrefill) return;
    if (params.get("city_id") && params.get("branch_id")) {
      setStep(2);
      setDidPrefill(true);
    }
  }, [params, didPrefill]);

  useEffect(() => { axios.get(`${API}/cities`).then((r) => setCities(r.data)); }, []);
  useEffect(() => { axios.get(`${API}/payments/config`).then((r) => setPayCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/addons`).then((r) => setAddons(r.data)).catch(() => setAddons([])); }, []);
  useEffect(() => {
    if (!city) return setBranches([]);
    axios.get(`${API}/branches`, { params: { city_id: city } }).then((r) => setBranches(r.data));
  }, [city]);
  useEffect(() => {
    if (!branch) return setRooms([]);
    axios.get(`${API}/rooms`, { params: { branch_id: branch } }).then((r) => setRooms(r.data));
  }, [branch]);
  // Fetch a preview image (first room image) for each sibling branch in the same city
  useEffect(() => {
    const siblings = branches.filter((b) => b.id !== branch);
    if (siblings.length === 0) {
      setOtherBranchImages({});
      return;
    }
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        siblings.map(async (b) => {
          // Prefer branch's own image_url if admin set one
          if (b.image_url) return [b.id, b.image_url];
          try {
            const r = await axios.get(`${API}/rooms`, { params: { branch_id: b.id } });
            const list = r.data || [];
            for (const rm of list) {
              if (rm.images && rm.images.length > 0) return [b.id, rm.images[0]];
              if (rm.image_url) return [b.id, rm.image_url];
            }
          } catch (e) { /* ignore */ }
          return [b.id, ""];
        })
      );
      if (!cancelled) {
        setOtherBranchImages(Object.fromEntries(results));
      }
    })();
    return () => { cancelled = true; };
  }, [branches, branch]);
  useEffect(() => {
    if (!room || !date) return setSlots([]);
    axios.get(`${API}/slots`, { params: { room_id: room, date } }).then((r) => setSlots(r.data));
  }, [room, date]);

  const selectedRoom = useMemo(() => rooms.find((r) => r.id === room), [rooms, room]);
  useEffect(() => {
    if ((selectedRoom?.payment_mode || "both") === "full_only" && paymentType === "advance") {
      setPaymentType("full");
    }
  }, [selectedRoom, paymentType]);
  // Auto-remove coupon if cart changes and bill falls below the coupon's minimum
  useEffect(() => {
    if (!couponApplied) return;
    const minAmt = Number(couponApplied.min_amount || 0);
    if (!minAmt) return;
    const liveSlotDisc = selectedSlots.reduce((sum, sl) => {
      const meta = slots.find((x) => x.slot === sl);
      return sum + (meta?.discount ? Number(meta.discount) : 0);
    }, 0);
    const liveSubtotal = Math.max(0, (selectedRoom?.price_per_slot || 0) * Math.max(selectedSlots.length, 1) - liveSlotDisc)
      + Math.max(0, Number(guests || 0) - (selectedRoom?.min_guests ?? 0)) * (selectedRoom?.extra_guest_price ?? 0) * Math.max(selectedSlots.length, 1)
      + addons.filter((a) => selectedAddons.includes(a.id)).reduce((s, a) => s + (Number(a.price) || 0), 0)
      + 500; 
    const liveTotal = Math.round(liveSubtotal * 1.18);
    if (liveTotal < minAmt) {
      setCouponApplied(false);
      setCoupon("");
      setCouponErr(`Coupon removed — bill dropped below ₹${minAmt}`);
    }
  }, [selectedAddons, selectedSlots, guests, selectedRoom, couponApplied, addons, slots]);
  const slotCount = Math.max(selectedSlots.length, 1);
  const minG = selectedRoom?.min_guests ?? 0;
  const extraP = selectedRoom?.extra_guest_price ?? 0;
  const extraGuests = Math.max(0, Number(guests || 0) - minG);
const extraGuestTotal = Math.round(extraGuests * extraP * slotCount);
// Sum of per-slot discounts (from /api/slots response) for currently selected slots
const slotDiscountTotal = selectedSlots.reduce((sum, sl) => {
  const meta = slots.find((x) => x.slot === sl);
  return sum + (meta?.discount ? Number(meta.discount) : 0);
}, 0);
const baseScreening = (selectedRoom?.price_per_slot || 0) * slotCount;
const price = Math.round(Math.max(0, baseScreening - slotDiscountTotal) + extraGuestTotal);
  const addonTotal = useMemo(
    () => addons.filter((a) => selectedAddons.includes(a.id)).reduce((s, a) => s + (Number(a.price) || 0), 0),
    [addons, selectedAddons]
  );
  // Mandatory decoration charge applied to every booking (pre-GST)
  const DECORATION_FEE = 500;

  // Helper: round half-up to 2 decimals (consistent rounding policy across the invoice)
  const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  // 1) Subtotal = screening + extras + add-ons + decoration (BEFORE any discount)
  const subtotal = r2(price + addonTotal + DECORATION_FEE);

  // ---------- Compute BOTH options independently (so card prices never collide) ----------

  // Helper to compute coupon discount on a given base
  const calcCoupon = (base) =>
    couponApplied
      ? Math.min(
          r2(base * Number(couponApplied.discount_percent || 0) / 100),
          Number(couponApplied.max_discount || Infinity)
        )
      : 0;

  // ----- Option A: UPFRONT (full) — 5% discount applies -----
  const upfront_fullPayDiscount = r2(subtotal * 0.05);
  const upfront_couponDiscount  = calcCoupon(subtotal - upfront_fullPayDiscount);
  const upfront_discount        = r2(upfront_fullPayDiscount + upfront_couponDiscount);
  const upfront_taxable         = r2(subtotal - upfront_discount);
  const upfront_gst             = r2(upfront_taxable * 0.18); // CGST 9% + SGST 9%
  const upfrontTotal            = r2(upfront_taxable + upfront_gst);

  // ----- Option B: ADVANCE — NO 5% discount -----
  const advance_couponDiscount  = calcCoupon(subtotal);
  const advance_taxable         = r2(subtotal - advance_couponDiscount);
  const advance_gst             = r2(advance_taxable * 0.18);
  const advanceTotal            = r2(advance_taxable + advance_gst);

  // Flat advance paid now (capped at the non-discounted total)
  const advanceAmount  = Math.min(Math.round(Number(payCfg.advance_amount || 750)), advanceTotal);
  const advanceBalance = Math.max(Math.round(advanceTotal - advanceAmount), 0);

  // ----- Active selection -----
  const fullPayDiscount = paymentType === "full" ? upfront_fullPayDiscount : 0;
  const couponDiscount  = paymentType === "full" ? upfront_couponDiscount  : advance_couponDiscount;
  const discount        = r2(fullPayDiscount + couponDiscount);
  const taxableValue    = paymentType === "full" ? upfront_taxable : advance_taxable;
  const cgst            = r2(taxableValue * 0.09);
  const sgst            = r2(taxableValue * 0.09);
  const gstAmount       = r2(cgst + sgst);
  const total           = paymentType === "full" ? upfrontTotal : advanceTotal;

  const payNow      = paymentType === "advance" ? advanceAmount  : upfrontTotal;
  const balanceDue  = paymentType === "advance" ? advanceBalance : 0;
  // Multi-slot consecutive toggle. Catalog order = order returned by /api/slots.
  const toggleSlot = (slotStr) => {
    const order = slots.map((s) => s.slot);
    setSelectedSlots((prev) => {
      // de-select if already last in selection
      if (prev.includes(slotStr)) {
        return prev.filter((s) => s !== slotStr);
      }
      const candidate = [...prev, slotStr];
      const idxs = candidate
        .map((s) => order.indexOf(s))
        .filter((i) => i >= 0)
        .sort((a, b) => a - b);
      const consecutive = idxs.every((v, i, a) => i === 0 || v - a[i - 1] === 1);
      if (!consecutive) return [slotStr]; // start over with just this one
      return idxs.map((i) => order[i]);
    });
  };
  const toggleAddon = (id) =>
    setSelectedAddons((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const applyCoupon = async () => {
  setCouponErr("");
  const code = coupon.trim().toUpperCase();
  if (!code) return setCouponErr("Enter a coupon code");
  try {
    // validate against the screening price (so % is on bill before GST)
    const r = await axios.get(`${API}/coupons/validate`, {
  params: { code, amount: total },
});
    setCouponApplied(r.data);    // now holds { code, discount_percent, discount_amount, ... }
    setCoupon(r.data.code);
  } catch (e) {
    setCouponApplied(false);
    setCouponErr(e.response?.data?.detail || "Invalid coupon code");
  }
};
const removeCoupon = () => { setCoupon(""); setCouponApplied(false); setCouponErr(""); };

  // Switch to a sibling branch in the same city (used by the "other branches" carousel)
  const switchBranch = (newBranchId) => {
    if (!newBranchId || newBranchId === branch) return;
    setBranch(newBranchId);
    setRoom("");
    setSelectedSlots([]);
    setSlots([]);
    // Keep the user on Step 2 so they can immediately pick a room
    setStep(2);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const canNext = () => {
    if (step === 1) return city && branch;
    if (step === 2) return room && date && selectedSlots.length > 0;
    if (step === 3) return !!auth && name.trim() && phone.trim() && guests > 0;
    return false;
  };

  const submit = async () => {
    setErr(""); setLoading(true);
    const bookingPayload = {
  branch_id: branch, room_id: room, customer_name: name.trim(),
  customer_phone: phone.trim(), customer_gstin: gstin.trim().toUpperCase(),
  customer_email: (email || auth?.user?.email || "").trim(),
  occasion, special_request: specialRequest.trim(),
  date, slot, slots: selectedSlots, guests: +guests, amount: total,
  add_ons: selectedAddons,
  payment_type: paymentType,
};
    try {
      // Mock mode – Razorpay not configured. Direct booking (development only).
      if (!payCfg.enabled) {
        const r = await axios.post(`${API}/bookings`, bookingPayload);
        setConfirmation(r.data);
        setStep(4);
        return;
      }

      // 1) Create order on backend
      const orderRes = await axios.post(`${API}/payments/order`, {
        branch_id: branch, room_id: room, date, slot, slots: selectedSlots, amount: total, payment_type: paymentType, add_ons: selectedAddons,
      });
      const order = orderRes.data;

      if (typeof window.Razorpay !== "function") {
        await new Promise((resolve, reject) => {
          const existing = document.getElementById("razorpay-checkout-script");
          if (existing) {
            existing.addEventListener("load", resolve);
            existing.addEventListener("error", () => reject(new Error("Payment gateway failed to load. Please refresh.")));
            return;
          }
          const s = document.createElement("script");
          s.id = "razorpay-checkout-script";
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.async = true;
          s.onload = resolve;
          s.onerror = () => reject(new Error("Payment gateway failed to load. Please refresh."));
          document.body.appendChild(s);
        });
        if (typeof window.Razorpay !== "function") {
          throw new Error("Payment gateway failed to load. Please refresh.");
        }
      }

      // 2) Open Razorpay checkout
      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: "The CineVibe",
          description: paymentType === "advance" ? `Advance · Private Screening · ${slot}` : `Private Screening · ${slot}`,
          order_id: order.order_id,
          prefill: { name: name.trim(), contact: phone.trim() },
          theme: { color: "#d4a574" },
          handler: async (res) => {
            try {
              const verify = await axios.post(`${API}/payments/verify`, {
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
                booking: bookingPayload,
              });
              setConfirmation(verify.data);
              setStep(4);
              resolve();
            } catch (e) {
              reject(new Error(e.response?.data?.detail || "Payment verification failed"));
            }
          },
          modal: {
            ondismiss: () => reject(new Error("Payment cancelled")),
          },
        });
        rzp.on("payment.failed", (resp) =>
          reject(new Error(resp?.error?.description || "Payment failed"))
        );
        rzp.open();
      });
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Could not complete booking");
    } finally { setLoading(false); }
  };

  const cityName = cities.find((c) => c.id === city)?.name || "";
  const branchName = branches.find((b) => b.id === branch)?.name || "";
  const branchObj = branches.find((b) => b.id === branch) || {};

  return (
    <div className="min-h-screen bg-[#0a0604] text-white" data-testid="book-page">
      <header className="border-b border-[#3a2a14]/60 bg-[#0a0604]/85 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1100px] mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#bfb393] hover:text-[#f4d089] text-sm" data-testid="back-home">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <div className="font-display text-[#f4d089]">The CineVibe · Book a Screening</div>
          <div className="w-20" />
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-5 py-8">
        <div className="flex items-center gap-3 sm:gap-6 mb-8 flex-wrap">
          <Step n={1} label="LOCATION" active={step === 1} done={step > 1} />
          <span className="h-px w-6 sm:w-10 bg-[#3a2a14]" />
          <Step n={2} label="ROOM & SLOT" active={step === 2} done={step > 2} />
          <span className="h-px w-6 sm:w-10 bg-[#3a2a14]" />
          <Step n={3} label="DETAILS" active={step === 3} done={step > 3} />
          <span className="h-px w-6 sm:w-10 bg-[#3a2a14]" />
          <Step n={4} label="CONFIRMED" active={step === 4} done={false} />
        </div>

        {err && <div className="mb-4 text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-md px-4 py-2" data-testid="book-error">{err}</div>}

        {/* STEP 1 */}
        {step === 1 && (
          <div data-testid="step-1-content">
            <h2 className="font-display text-3xl text-white mb-1">Where would you like to vibe?</h2>
            <p className="text-sm text-[#a89878] mb-6">Pick your city and outlet</p>

            <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><MapPin size={12} /> City</div>
            {cities.length === 0 ? (
              <div className="text-sm text-[#a89878] border border-[#3a2a14] rounded-md p-4">
                No cities yet. Ask the admin to add cities at <Link to="/admin" className="text-[#f4d089] underline">/admin</Link>.
              </div>
            ) : (
              <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
                {cities.map((c) => (
                  <Card key={c.id} active={city === c.id} onClick={() => { setCity(c.id); setBranch(""); }} testid={`city-${c.id}`}>
                    <div className="text-white font-semibold">{c.name}</div>
                    <div className="text-xs text-[#a89878] mt-1">Tap to select</div>
                  </Card>
                ))}
              </div>
            )}

            {city && (
              <>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><Building2 size={12} /> Outlet / Location</div>
                {branches.length === 0 ? (
                  <div className="text-sm text-[#a89878] border border-[#3a2a14] rounded-md p-4">No outlets in this city yet.</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {branches.map((b) => (
                      <Card key={b.id} active={branch === b.id} onClick={() => setBranch(b.id)} testid={`branch-${b.id}`}>
                        <div className="text-white font-semibold">{b.name}</div>
                        <div className="text-xs text-[#a89878] mt-1">{b.address || "—"}</div>
                        <div className="text-[10px] text-[#f4d089] mt-2">Invoice: {b.invoice_prefix}-XXXX</div>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div data-testid="step-2-content">
            <h2 className="font-display text-3xl text-white mb-1">Pick a room & slot</h2>
            <p className="text-sm text-[#a89878] mb-6">{cityName} · {branchName}</p>

            <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><DoorOpen size={12} /> Room</div>
            {(() => { return null; })()}
            {rooms.length === 0 ? (
              <div className="text-sm text-[#a89878] border border-[#3a2a14] rounded-md p-4 mb-6">No rooms set up for this outlet yet.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {rooms.map((r) => (
                  <Card key={r.id} active={room === r.id} onClick={() => { setRoom(r.id); setSelectedSlots([]); }} testid={`room-${r.id}`}>
                    {(() => {
  const imgs =
    (r.images && r.images.length)
      ? r.images
      : (r.image_url ? [r.image_url] : []);

  return imgs.length > 0 ? (
    <div className="relative">
      <RoomImages images={imgs} alt={r.name} />

      {/* video badges over the image */}
      <div className="absolute bottom-5 left-2 right-2 flex justify-between pointer-events-none">
        {r.video_link && (
          <a
            href={r.video_link}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="pointer-events-auto bg-white/90 text-[#1a0f06] text-[11px] font-semibold px-2 py-1 rounded-full"
            data-testid={`room-video-${r.id}`}
          >
            ▶ Theatre Video
          </a>
        )}

        {branchObj.video_link && (
          <a
            href={branchObj.video_link}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="pointer-events-auto ml-auto bg-white/90 text-[#1a0f06] text-[11px] font-semibold px-2 py-1 rounded-full"
            data-testid={`branch-video-${r.id}`}
          >
            ▶ Branch Video
          </a>
        )}
      </div>
    </div>
  ) : null;
})()}

                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-white font-semibold">{r.name}</div>
                        <div className="text-xs text-[#a89878] mt-1 flex items-center gap-1">
                          <MapPin size={11} /> {branchObj.name || branchObj.city}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[#f4d089] font-display text-lg">₹{r.price_per_slot}</div>
                        <div className="text-[10px] text-[#a89878]">per slot</div>
                      </div>
                    </div>

                    {/* Feature bar — driven by superadmin room/branch settings */}
                    <div className="mt-3 grid grid-cols-2 gap-y-1.5 gap-x-3 text-[11px] text-[#bfb393]" data-testid={`room-features-${r.id}`}>
                      <div className="flex items-center gap-1.5">
                        <Users size={12} className="text-[#f4d089]" />
                        <span>Max {r.capacity} People</span>
                      </div>
                      {branchObj.petpooja_menu_url && (
                        <div className="flex items-center gap-1.5">
                          <Coffee size={12} className="text-[#f4d089]" />
                          <span>Food Available*</span>
                        </div>
                      )}
                     
                      <div className="flex items-center gap-1.5">
                        <PartyPopper size={12} className="text-[#f4d089]" />
                        <span>Decor ₹500 extra</span>
                      </div>
                      <div className="flex items-center gap-1.5 col-span-2">
                        <Cake size={12} className="text-[#f4d089]" />
                        <span>Add Cake, Fog entry etc in next step</span>
                      </div>
                      
                      {(r.min_guests ?? 0) > 0 && (r.extra_guest_price ?? 0) > 0 && (
                        <div className="flex items-center gap-1.5 col-span-2">
                          <Tag size={12} className="text-[#f4d089]" />
                          <span>Base for {r.min_guests} · ₹{r.extra_guest_price}/extra guest</span>
                        </div>
                      )}
                    </div>

                    {/* Maps + Food links (branch-level) */}
                    {(branchObj.map_link || branchObj.petpooja_menu_url) && (
                      <div className="flex gap-2 mt-3">
                        {branchObj.map_link && (
                          <a
                            href={branchObj.map_link}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 text-center text-[11px] font-semibold bg-[#1a0f06] border border-[#3a2a14] text-[#f4d089] py-2 rounded hover:border-[#f4d089]/60"
                            data-testid={`room-maps-${r.id}`}
                          >📍 MAPS</a>
                        )}
                        {branchObj.petpooja_menu_url && (
                          <a
                            href={branchObj.petpooja_menu_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 text-center text-[11px] font-semibold bg-[#1a0f06] border border-[#3a2a14] text-[#f4d089] py-2 rounded hover:border-[#f4d089]/60"
                            data-testid={`room-food-${r.id}`}
                          >🍽 FOOD</a>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><CalIcon size={12} /> Date</div>
                <Input type="date" min={todayStr()} value={date} onChange={(e) => { setDate(e.target.value); setSelectedSlots([]); }} data-testid="date-input" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><Clock size={12} /> Slot</div>
                {!room || !date ? (
                  <div className="text-sm text-[#a89878] border border-[#3a2a14] rounded-md p-3">Select room & date first</div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {slots.map((s) => (
                      <div key={s.slot} className="flex flex-col">
                        <button
                          disabled={!s.available}
                          onClick={() => toggleSlot(s.slot)}
                          data-testid={`slot-${s.slot}`}
                          className={`px-3 py-3 rounded-md text-sm font-semibold border transition ${
                            selectedSlots.includes(s.slot) ? "border-[#f4d089] bg-[#1a0f06] text-[#f4d089]" :
                            !s.available ? "border-[#3a2a14] bg-[#0a0604] text-[#5a4d36] cursor-not-allowed" :
                            "border-[#3a2a14] text-[#bfb393] hover:border-[#d4a574]"
                          }`}
                        >{s.slot}{!s.available && <span className="block text-[10px]">Booked</span>}</button>
                        {s.discount > 0 && s.available && (
                          <div
                            className="text-[11px] font-semibold text-[#7ad99c] mt-1 text-center"
                            data-testid={`slot-discount-${s.slot}`}
                          >
                            Rs {s.discount} less
                          </div>
                        )}
                      </div>
                    ))}
                    {selectedSlots.length > 1 && (
                      <div className="col-span-full text-[11px] text-[#a89878]">
                        {selectedSlots.length} consecutive slots selected · click again to deselect.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

{/* Explore other branches in the same city */}
        {step === 2 && city && branches.filter((b) => b.id !== branch).length > 0 && (
          <div className="mt-12 pt-8 border-t border-[#3a2a14]" data-testid="other-branches-section">
            <div className="mb-5">
              <h3 className="font-display text-2xl text-white">
                Explore {branches.filter((b) => b.id !== branch).length} other{" "}
                {branches.filter((b) => b.id !== branch).length === 1 ? "venue" : "venues"} in{" "}
                <span className="text-[#f4d089]">
                  {cities.find((c) => c.id === city)?.name || "this city"}
                </span>
              </h3>
              <p className="text-xs text-[#a89878] mt-1">
                Tap a branch to see its rooms — your date stays selected.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {branches
                .filter((b) => b.id !== branch)
                .map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => switchBranch(b.id)}
                    data-testid={`other-branch-${b.id}`}
                    className="text-left rounded-lg border border-[#3a2a14] bg-[#0d0905] hover:border-[#f4d089]/60 hover:shadow-[0_0_0_2px_rgba(244,208,137,0.15)] transition overflow-hidden"
                  >
                    {(b.image_url || otherBranchImages[b.id]) ? (
                      <div className="h-36 w-full bg-[#0a0604] overflow-hidden">
                        <img
                          src={b.image_url || otherBranchImages[b.id]}
                          alt={b.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          data-testid={`other-branch-img-${b.id}`}
                        />
                      </div>
                    ) : (
                      <div className="h-36 w-full bg-gradient-to-br from-[#1a0f06] to-[#0a0604] flex items-center justify-center">
                        <Building2 size={32} className="text-[#3a2a14]" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="text-white font-semibold flex items-center gap-2">
                        <Building2 size={14} className="text-[#f4d089]" /> {b.name}
                      </div>
                      {b.address && (
                        <div className="text-[11px] text-[#a89878] mt-1 flex items-start gap-1">
                          <MapPin size={11} className="mt-0.5 shrink-0" /> {b.address}
                        </div>
                      )}
                      <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[#f4d089] flex items-center gap-1">
                        View venue <ArrowRight size={12} />
                      </div>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        )}



        {/* STEP 3 */}
        {step === 3 && (
          <div data-testid="step-3-content">
            <h2 className="font-display text-3xl text-white mb-1">Your details</h2>
            <p className="text-sm text-[#a89878] mb-6">We'll send confirmation on WhatsApp / SMS</p>

            {/* Auth gate — required before payment */}
            {!auth ? (
              <div className="rounded-lg border border-[#f4d089]/40 bg-[#1a0f06] p-5 mb-6" data-testid="auth-gate">
                <div className="flex items-start gap-3 mb-4">
                  <Lock size={18} className="text-[#f4d089] mt-0.5" />
                  <div>
                    <div className="text-white font-semibold">Sign up or log in to continue</div>
                  </div>
                </div>

                {authErr && <div className="text-xs text-red-400 mb-3" data-testid="auth-error">{authErr}</div>}

                {authMethod === "password" ? (
                  <div className="space-y-3" data-testid="auth-password-form">
                    <div className="flex gap-2 text-[11px]">
                      <button onClick={() => setPwMode("login")} data-testid="pw-tab-login"
                        className={`px-2.5 py-1 rounded-full border ${pwMode === "login" ? "bg-[#3a2a14] text-[#f4d089] border-[#f4d089]/40" : "border-[#3a2a14] text-[#a89878]"}`}>Log in</button>
                      <button onClick={() => setPwMode("signup")} data-testid="pw-tab-signup"
                        className={`px-2.5 py-1 rounded-full border ${pwMode === "signup" ? "bg-[#3a2a14] text-[#f4d089] border-[#f4d089]/40" : "border-[#3a2a14] text-[#a89878]"}`}>Sign up</button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><Phone size={12} /> Mobile Number</div>
                        <Input value={pwPhone} onChange={(e) => setPwPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                          placeholder="10-digit mobile" inputMode="numeric" maxLength={10} data-testid="pw-phone" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><KeyRound size={12} /> Password</div>
                        <Input type="password" value={pwPassword} onChange={(e) => setPwPassword(e.target.value)}
                          placeholder="Min. 6 characters" data-testid="pw-password" />
                      </div>
                      {pwMode === "signup" && (
                        <>
                          <div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><User size={12} /> Full Name</div>
                            <Input value={pwName} onChange={(e) => setPwName(e.target.value)} placeholder="Your full name" data-testid="pw-name" />
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2">Email (optional)</div>
                            <Input value={pwEmail} onChange={(e) => setPwEmail(e.target.value)} placeholder="you@example.com" data-testid="pw-email" />
                          </div>
                        </>
                      )}
                    </div>
                    <Btn onClick={submitPassword} disabled={pwBusy} data-testid="pw-submit">
                      {pwBusy ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Please wait…</> : (pwMode === "signup" ? "Sign up & Continue" : "Log in & Continue")}
                    </Btn>
                  </div>
                ) : authMode === "phone" ? (
                  <div className="grid sm:grid-cols-3 gap-3 items-end">
                    <div className="sm:col-span-2">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><Phone size={12} /> Mobile Number</div>
                      <Input
                        value={otpPhone}
                        onChange={(e) => setOtpPhone(e.target.value)}
                        placeholder="10-digit mobile"
                        inputMode="numeric"
                        maxLength={10}
                        data-testid="auth-phone"
                      />
                    </div>
                    <Btn onClick={sendOtp} disabled={otpSending || otpPhone.replace(/\D/g, "").length < 10} data-testid="send-otp-btn">
                      {otpSending ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Sending…</> : "Send OTP"}
                    </Btn>
                  </div>
                ) : (
                  <div>
                    <div className="text-xs text-[#bfb393] mb-3">
                      OTP sent to <span className="text-[#f4d089] font-semibold">+91 {otpPhone}</span>
                      <button onClick={() => { setAuthMode("phone"); setOtpCode(""); setOtpHint(""); }} className="ml-2 underline text-[#a89878] hover:text-[#f4d089]" data-testid="change-phone">change</button>
                    </div>
                    {otpHint && (
                      <div className="text-[11px] text-[#7ad99c] border border-[#7ad99c]/30 bg-[#0d1a10] rounded px-2 py-1 mb-3" data-testid="otp-hint">
                        {otpHint}
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><User size={12} /> Full Name (new users)</div>
                        <Input value={otpName} onChange={(e) => setOtpName(e.target.value)} placeholder="Optional" data-testid="auth-name" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2">Email (optional)</div>
                        <Input value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} placeholder="you@example.com" data-testid="auth-email" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 items-end">
                      <div className="sm:col-span-2">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><KeyRound size={12} /> 6-digit OTP</div>
                        <Input
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="••••••"
                          inputMode="numeric"
                          maxLength={6}
                          data-testid="auth-otp"
                        />
                      </div>
                      <Btn onClick={verifyOtp} disabled={otpVerifying || otpCode.length !== 6} data-testid="verify-otp-btn">
                        {otpVerifying ? <><Loader2 size={14} className="inline mr-1 animate-spin" /> Verifying…</> : "Verify & Continue"}
                      </Btn>
                    </div>
                    <button onClick={sendOtp} disabled={otpSending} className="text-xs text-[#bfb393] hover:text-[#f4d089] mt-3 underline" data-testid="resend-otp">
                      {otpSending ? "Sending…" : "Resend OTP"}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-[#7ad99c]/40 bg-[#0d1a10] p-3 mb-6 flex items-center justify-between" data-testid="auth-banner">
                <div className="flex items-center gap-2 text-sm">
                  <ShieldCheck size={16} className="text-[#7ad99c]" />
                  <span className="text-white">Verified · <span className="text-[#7ad99c] font-semibold">+91 {auth.user.phone}</span>{auth.user.name ? ` · ${auth.user.name}` : ""}</span>
                </div>
                <button onClick={logout} className="text-xs text-[#a89878] hover:text-[#f4d089] flex items-center gap-1" data-testid="logout-btn">
                  <LogOut size={12} /> Log out
                </button>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><User size={12} /> Full Name</div>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aman Singh" data-testid="cust-name" />
                </div>
                <div>
  <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><Phone size={12} /> Phone</div>
  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" data-testid="cust-phone" disabled={!!auth} />
</div>
<div>
  <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2">Email</div>
  <Input
    type="email"
    value={email}
    onChange={(e) => setEmail(e.target.value)}
    placeholder="you@example.com"
    data-testid="cust-email"
  />
</div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2">GSTIN (optional)</div>
                  <Input
                    value={gstin}
                    onChange={(e) => setGstin(e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 15))}
                    placeholder="15-char GSTIN for tax invoice"
                    maxLength={15}
                    data-testid="cust-gstin"
                  />
                </div>
                <div>
  <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><Users size={12} /> Guests</div>
  <Input
    type="number"
    min={minG || 1}
    max={selectedRoom?.capacity || 12}
    value={guests}
    onChange={(e) => {
      const v = e.target.value;
      if (v === "") { setGuests(""); return; }
      const n = Math.max(minG || 1, Math.min(Number(v), selectedRoom?.capacity || 12));
      setGuests(n);
    }}
    onBlur={() => {
      const n = Math.max(minG || 1, Number(guests || 0));
      setGuests(n);
    }}
    data-testid="cust-guests"
  />
  {selectedRoom && (
    <div className="text-[10px] text-[#a89878] mt-1">
      Minimum {minG} guest{minG > 1 ? "s" : ""} · Room capacity: {selectedRoom.capacity}
    </div>
  )}
</div>
                <div className="sm:col-span-2">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2"><Sparkles size={12} /> Occasion (optional)</div>
                  <select
                    value={occasion}
                    onChange={(e) => setOccasion(e.target.value)}
                    data-testid="cust-occasion"
                    className="w-full bg-[#0d0905] border border-[#3a2a14] rounded-md px-3 py-2 text-white focus:border-[#f4d089] outline-none"
                  >
                    <option value="">Select an occasion…</option>
                    <option value="Birthday">Birthday</option>
                    <option value="Anniversary">Anniversary</option>
                    <option value="Romantic Date">Romantic Date</option>
                    <option value="Friends Get Together">Friends Get Together</option>
                    <option value="Others">Others</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-2 flex items-center gap-2">Special Request / Notes (optional)</div>
                  <textarea
                    value={specialRequest}
                    onChange={(e) => setSpecialRequest(e.target.value.slice(0, 500))}
                    placeholder=""
                    rows={3}
                    maxLength={500}
                    data-testid="cust-special-request"
                    className="w-full bg-[#0d0905] border border-[#3a2a14] rounded-md px-3 py-2 text-white text-sm focus:border-[#f4d089] outline-none resize-none"
                  />
                  <div className="text-[10px] text-[#a89878] mt-1 text-right">{specialRequest.length}/500</div>
                </div>
              </div>

              {/* Need Help? */}
              <div className="self-start rounded-lg border border-[#3a2a14] bg-[#0d0905] p-5" data-testid="need-help-card">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-3">Need Help?</div>
                <p className="text-sm text-[#a89878] mb-4">Our team is happy to assist with bookings, custom decor or any questions.</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="tel:+919876543210"
                    data-testid="help-call-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#f4d089]/40 bg-[#1a0f06] text-[#f4d089] hover:bg-[#2a1a0a] transition text-sm"
                  >
                    <Phone size={14} /> Call Us
                  </a>
                  <a
                    href={waUrl("Hi The CineVibe! I need some help with my booking.")}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="help-whatsapp-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#7ad99c]/40 bg-[#0f1a10] text-[#7ad99c] hover:bg-[#152a18] transition text-sm"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.518 5.276l-.999 3.648 3.97-.623z"/></svg>
                    WhatsApp Us
                  </a>
                </div>
              </div>

              <div className="rounded-lg border border-[#3a2a14] bg-[#0d0905] p-5">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-3 flex items-center gap-2">
                  <Sparkles size={12} /> Enhance your screening (optional)
                </div>
                {addons.length === 0 ? (
                  <div className="text-xs text-[#a89878]">No add-ons available right now.</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-2 mb-5" data-testid="addons-list">
                    {addons.map((a) => {
                      const sel = selectedAddons.includes(a.id);
                      return (
                        <button
  key={a.id}
  type="button"
  onClick={() => toggleAddon(a.id)}
  data-testid={`addon-${a.id}`}
  className={`text-left rounded-md border p-3 transition flex items-start gap-3 ${
    sel
      ? "border-[#f4d089] bg-[#1a0f06] shadow-[0_0_0_2px_rgba(244,208,137,0.2)]"
      : "border-[#3a2a14] bg-[#0a0604] hover:border-[#d4a574]/60"
  }`}
>
  <div className={`w-5 h-5 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 ${sel ? "bg-[#f4d089] border-[#f4d089] text-[#1a0f06]" : "border-[#3a2a14] text-transparent"}`}>
    {sel ? <Check size={13} strokeWidth={3} /> : <Plus size={12} className="text-[#a89878]" />}
  </div>

  {/* NEW: addon thumbnail */}
  {a.image ? (
    <img
      src={a.image}
      alt={a.name}
      className="w-11 h-11 rounded object-cover border border-[#3a2a14] flex-shrink-0"
      loading="lazy"
      onError={(e) => { e.currentTarget.style.display = "none"; }}
    />
  ) : (
    <div className="w-11 h-11 rounded border border-[#3a2a14] bg-[#1a0f06] flex items-center justify-center flex-shrink-0">
      <Sparkles size={14} className="text-[#a89878]" />
    </div>
  )}

  <div className="flex-1 min-w-0">
    <div className="flex items-center justify-between gap-2">
      <div className="text-white text-sm font-semibold truncate">{a.name}</div>
      <div className="text-[#f4d089] text-sm font-display whitespace-nowrap">+₹{a.price}</div>
    </div>
    {a.description && <div className="text-[10px] text-[#a89878] mt-0.5 line-clamp-2">{a.description}</div>}
  </div>
</button>
                      );
                    })}
                  </div>
                )}

                <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-3">Booking Summary</div>
                <SummaryRow label="City" value={cityName} />
                <SummaryRow label="Outlet" value={branchName} />
                <SummaryRow label="Room" value={selectedRoom?.name} />
                <SummaryRow label="Date" value={date} />
                <SummaryRow label={selectedSlots.length > 1 ? "Slots" : "Slot"} value={selectedSlots.join(", ")} />
                <div className="border-t border-[#3a2a14] mt-3 pt-3">
                  <SummaryRow
                    label={`Room charge (${slotCount} slot${slotCount > 1 ? "s" : ""} × ${minG} guests)`}
                    value={`₹${(selectedRoom?.price_per_slot || 0) * slotCount}`}
                  />
                  {extraGuests > 0 && (
                    <SummaryRow
                      label={`Extra guests (${extraGuests} × ₹${extraP}${slotCount > 1 ? ` × ${slotCount} slots` : ""})`}
                      value={`₹${extraGuestTotal}`}
                      data-testid="extra-guest-row"
                    />
                  )}
                  <SummaryRow
                    label="Mandatory Decoration Charges"
                    value={`₹${DECORATION_FEE}`}
                    data-testid="decoration-row"
                  />
                  {addons.filter((a) => selectedAddons.includes(a.id)).map((a) => (
                    <SummaryRow key={a.id} label={`+ ${a.name}`} value={`₹${a.price}`} muted />
                  ))}
                  {addonTotal > 0 && (
                    <SummaryRow label="Add-ons subtotal" value={`₹${addonTotal}`} />
                  )}
                  {slotDiscountTotal > 0 && (
                    <div className="flex items-center justify-between text-sm py-1" data-testid="discount-slot">
                      <div className="text-[#7ad99c]">Slot Offer (Early/Late slot)</div>
                      <div className="text-[#7ad99c]">- ₹{slotDiscountTotal}</div>
                    </div>
                  )}
                  {fullPayDiscount > 0 && (
                    <div className="flex items-center justify-between text-sm py-1" data-testid="discount-fullpay">
                      <div className="text-[#7ad99c]">Upfront Payment Discount (5%)</div>
                      <div className="text-[#7ad99c]">- ₹{fullPayDiscount}</div>
                    </div>
                  )}
                  {couponDiscount > 0 && (
                    <div className="flex items-center justify-between text-sm py-1" data-testid="discount-coupon">
                      <div className="text-[#7ad99c]">Coupon ({couponApplied?.code}, {couponApplied?.discount_percent}%)</div>
                      <div className="text-[#7ad99c]">- ₹{couponDiscount}</div>
                    </div>
                  )}
                  <SummaryRow label="Subtotal" value={`₹${subtotal.toFixed(2)}`} />
                  {discount > 0 && (
                    <SummaryRow label="Less: Discount" value={`- ₹${discount.toFixed(2)}`} />
                  )}
                  <SummaryRow label="Taxable Value" value={`₹${taxableValue.toFixed(2)}`} />
                  <SummaryRow label="CGST (9%)" value={`₹${cgst.toFixed(2)}`} muted />
                  <SummaryRow label="SGST (9%)" value={`₹${sgst.toFixed(2)}`} muted />
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-white font-semibold">Total</div>
                    <div className="font-display text-2xl text-[#f4d089]" data-testid="total-amount">₹{total}</div>
                  </div>

                  {/* Coupon input */}
                  <div className="mt-4" data-testid="coupon-section">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-1">Have a coupon?</div>
                    {couponApplied ? (
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[#7ad99c] text-xs">
                          Coupon <b>{couponApplied.code}</b> applied — {couponApplied.discount_percent}% off
                          (₹{couponDiscount} saved)
                        </div>
                        <button type="button" onClick={removeCoupon} className="text-[11px] text-[#f4d089] underline" data-testid="coupon-remove">Remove</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={coupon}
                          onChange={(e) => setCoupon(e.target.value)}
                          placeholder="Enter coupon code"
                          data-testid="coupon-input"
                          className="flex-1 bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white placeholder:text-[#6b5a3a] focus:outline-none focus:border-[#d4a574]"
                        />
                        <button
                          type="button"
                          onClick={applyCoupon}
                          data-testid="coupon-apply"
                          className="bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold px-4 py-2 rounded-md text-sm"
                        >Apply</button>
                      </div>
                    )}
                    {couponErr && <div className="text-[11px] text-[#e8456a] mt-1" data-testid="coupon-error">{couponErr}</div>}
                  </div>

                  {/* Payment option selector */}
                  <div className="mt-5 space-y-2" data-testid="payment-options">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#f4d089] mb-1">Choose Payment Option</div>
                    <button
                      type="button"
                      onClick={() => setPaymentType("full")}
                      data-testid="pay-option-full"
                      className={`w-full text-left rounded-md border p-3 transition ${
                        paymentType === "full"
                          ? "border-[#f4d089] bg-[#1a0f06] shadow-[0_0_0_2px_rgba(244,208,137,0.25)]"
                          : "border-[#3a2a14] bg-[#0a0604] hover:border-[#d4a574]/60"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white font-semibold">Upfront Payment <span className="text-[#7ad99c] text-[11px] font-bold ml-1">· 5% OFF</span></div>
                          <div className="text-[11px] text-[#a89878] mt-0.5">Pay the complete amount now & get instant 5% off on the total bill.</div>
                        </div>
                        <div className="font-display text-[#f4d089] text-lg">₹{upfrontTotal}</div>
                      </div>
                    </button>
                    {((selectedRoom?.payment_mode || "both") !== "full_only") && (
                    <button
                      type="button"
                      onClick={() => setPaymentType("advance")}
                      data-testid="pay-option-advance"
                      className={`w-full text-left rounded-md border p-3 transition ${
                        paymentType === "advance"
                          ? "border-[#f4d089] bg-[#1a0f06] shadow-[0_0_0_2px_rgba(244,208,137,0.25)]"
                          : "border-[#3a2a14] bg-[#0a0604] hover:border-[#d4a574]/60"
                      }`}
                    >
                       <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-white font-semibold">Advance Payment · ₹{advanceAmount}</div>
                          <div className="text-[11px] text-[#a89878] mt-0.5">
                            Pay ₹{advanceAmount} flat advance now to block the slot (covers screening + add-ons). Balance of ₹{advanceBalance} at the venue or online via <span className="text-[#f4d089]">My Account</span>.
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-display text-[#f4d089] text-lg">₹{advanceAmount}</div>
                          <div className="text-[10px] text-[#a89878]">+ ₹{advanceBalance} later</div>
                        </div>
                      </div>
                    </button>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#3a2a14]">
                    <div className="text-white font-semibold">Paying Now</div>
                    <div className="font-display text-2xl text-[#f4d089]" data-testid="pay-now-amount">₹{payNow}</div>
                  </div>
                  {balanceDue > 0 && (
                    <div className="flex items-center justify-between mt-1">
                      <div className="text-[#a89878] text-sm">Balance (at venue / later online)</div>
                      <div className="text-[#f4d089] text-sm" data-testid="balance-due-amount">₹{balanceDue}</div>
                    </div>
                  )}
                  <div className="text-[10px] text-[#a89878] mt-1">Food/F&B billed separately by café (GST 5%)</div>
                  <div className="text-[10px] text-[#a89878] mt-2 flex items-center gap-2" data-testid="payment-mode">
                    {payCfg.enabled ? (
                      <>🔒 Secure payment via <span className="text-[#f4d089] font-semibold">Razorpay</span></>
                    ) : (
                      <>⚠ Test mode — payment gateway not configured</>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        )}

        {/* STEP 4 */}
        {step === 4 && confirmation && (
          <div className="text-center py-8" data-testid="step-4-content">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#143c20] border border-[#7ad99c]/40 flex items-center justify-center mb-5">
              <CheckCircle2 size={40} className="text-[#7ad99c]" />
            </div>
            <h2 className="font-display text-3xl text-white">Booking Confirmed!</h2>
            <p className="text-[#a89878] mt-2">A confirmation has been recorded. We'll be in touch shortly.</p>

            <div className="max-w-md mx-auto mt-6 rounded-lg border border-[#3a2a14] bg-[#0d0905] p-5 text-left">
              <div className="flex items-center justify-between border-b border-[#3a2a14] pb-3 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#a89878]">
                    {confirmation.invoice_no ? "Invoice No." : "Booking Ref."}
                  </div>
                  <div className="font-display text-xl text-[#f4d089]" data-testid="invoice-no">
                    {confirmation.invoice_no || confirmation.id.slice(0, 8).toUpperCase()}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-[#a89878]">Total</div>
                  <div className="font-display text-xl text-[#f4d089]">₹{confirmation.amount}</div>
                </div>
              </div>
              <SummaryRow label="Name" value={confirmation.customer_name} />
              <SummaryRow label="Phone" value={confirmation.customer_phone} />
              <SummaryRow label="Date" value={confirmation.date} />
              <SummaryRow label={(confirmation.slots && confirmation.slots.length > 1) ? "Slots" : "Slot"} value={(confirmation.slots && confirmation.slots.length ? confirmation.slots.join(", ") : confirmation.slot)} />
              <SummaryRow label="Guests" value={confirmation.guests} />
              {confirmation.extra_guests > 0 && (
                <SummaryRow
                  label={`Extra guests (${confirmation.extra_guests} × ₹${confirmation.extra_guest_price})`}
                  value={`₹${confirmation.extra_guest_total}`}
                  data-testid="confirmation-extra-guest"
                />
              )}
              <SummaryRow label="GST (18% incl.)" value={`₹${confirmation.gst_amount}`} muted />
              {confirmation.payment_type === "advance" && (
                <>
                  <div className="mt-3 pt-3 border-t border-[#3a2a14]">
                    <SummaryRow label="Paid now (Advance)" value={`₹${Math.round(Number(confirmation.paid_amount || confirmation.advance_amount || 0))}`} />
<SummaryRow label="Balance due" value={`₹${Math.round(Number(confirmation.balance_due || 0))}`} />
                    <div className="text-[11px] text-[#f4d089] mt-2" data-testid="balance-note">
                      Pay the balance of ₹{confirmation.balance_due || 0} at the venue, or anytime online via
                      <Link to="/account" className="underline ml-1">My Account</Link>
                      (login with your phone +91 {confirmation.customer_phone} and password).
                    </div>
                  </div>
                </>
              )}
              {confirmation.payment_status === "paid" && confirmation.payment_type !== "advance" && (
                <SummaryRow label="Payment" value={`✅ Paid · ${confirmation.payment_id}`} />
              )}
              <div className="text-[10px] text-[#a89878] mt-3 text-center">Booking ID: {confirmation.id}</div>
            </div>

            <div className="mt-6 flex gap-3 justify-center flex-wrap">
              {Number(confirmation.balance_due || 0) === 0 && (
                <a
                  href={`${API}/invoices/${confirmation.id}`}
                  target="_blank" rel="noreferrer"
                  className="bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold px-5 py-2.5 rounded-md text-sm"
                  data-testid="download-invoice"
                >Download Invoice (PDF)</a>
              )}
              <a
                href={waUrl(`✅ The CineVibe Booking Confirmed\nInvoice: ${confirmation.invoice_no}\nName: ${confirmation.customer_name}\nDate: ${confirmation.date} | Slots: ${(confirmation.slots && confirmation.slots.length ? confirmation.slots.join(", ") : confirmation.slot)}\nGuests: ${confirmation.guests}\nAmount: ₹${confirmation.amount} (GST 18% incl.)\nInvoice PDF: ${process.env.REACT_APP_BACKEND_URL}/api/invoices/${confirmation.id}`)}
                target="_blank" rel="noreferrer"
                className="bg-[#25D366] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#20bf5a] transition"
                data-testid="share-whatsapp"
              >Send to WhatsApp</a>
              <button onClick={() => nav("/")} className="border border-[#d4a574]/40 text-[#f4d089] px-5 py-2.5 rounded-md text-sm" data-testid="back-home-btn">Back to Home</button>
            </div>
          </div>
        )}

        {step < 4 && (
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#3a2a14]">
            <button onClick={back} disabled={step === 1} className="text-sm text-[#bfb393] disabled:opacity-30 flex items-center gap-1" data-testid="prev-step">
              <ArrowLeft size={14} /> Back
            </button>
            {step < 3 ? (
              <Btn onClick={next} disabled={!canNext()} data-testid="next-step">
                Continue <ArrowRight size={14} className="inline ml-1" />
              </Btn>
            ) : (
              <Btn
  onClick={() => { setTermsAccepted(false); setShowTerms(true); }}
  disabled={!canNext() || loading}
  data-testid="confirm-booking"
>
  {loading ? (
    <>
      <Loader2 size={14} className="inline mr-1 animate-spin" />
      {payCfg.enabled ? "Processing payment…" : "Confirming…"}
    </>
  ) : (
    payCfg.enabled
      ? `Pay ₹${payNow} & Confirm`
      : `Confirm Booking · ₹${payNow}`
  )}
</Btn>
)}
          </div>
        )}

        {/* Terms & Conditions Modal */}
        {showTerms && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            data-testid="terms-modal"
          >
            <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-[#0a1a3f] text-white px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold tracking-wide">TERMS & CONDITIONS</h2>
                <button
                  onClick={() => setShowTerms(false)}
                  className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center hover:bg-white/10"
                  data-testid="close-terms-modal"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              





                <div className="overflow-y-auto px-6 py-5 text-[#1a1a1a] text-sm space-y-3">

                <ol className="list-decimal pl-5 space-y-3">
                  <li>Outside food &amp; beverages are <span className="text-red-600 font-semibold">not permitted</span>.</li>
                  <li>Smoking, vaping, consumption of alcohol, narcotics, or any prohibited substance is <span className="text-red-600 font-semibold">strictly prohibited</span>. Violation may attract a <span className="text-red-600 font-semibold">penalty of up to ₹2,000</span> and immediate termination of the booking.</li>
                  <li>Valid <span className="font-semibold">Government-issued ID</span> may be required for entry and age verification.</li>
                  <li>CineVibe does not provide OTT subscriptions, movies, TV channels, or copyrighted content. Guests must use their <span className="font-semibold">own valid OTT accounts</span> or <span className="font-semibold">legally owned/downloaded content</span> in accordance with applicable Indian copyright laws.</li>
                  <li>Recording, live streaming, screen capture, or unauthorized sharing of copyrighted content is <span className="text-red-600 font-semibold">strictly prohibited</span>.</li>
                  <li>Any damage to property, equipment, furnishings, decorations, or fixtures shall be <span className="font-semibold">recoverable from the guest</span>.</li>
                  <li>Please maintain cleanliness inside the screening room. Additional cleaning charges may apply where necessary.</li>
                  <li>Party poppers, snow sprays, cold fire, confetti, crackers, candles, and similar items are <span className="text-red-600 font-semibold">strictly prohibited</span>.</li>
                  <li>Pets are not allowed.</li>
                  <li>Management reserves the right to refuse entry or terminate a booking <span className="text-red-600 font-semibold">without refund</span> in case of misconduct, violation of rules, illegal activity, safety concerns, or disturbance to other guests.</li>
                  <li>In case of a prolonged power interruption or technical issue <span className="text-red-600 font-semibold">exceeding 15 minutes</span>, CineVibe may offer an alternate slot or rescheduling support, subject to availability.</li>
                </ol>

                <div className="pt-4">
                  <div className="inline-block bg-[#0a1a3f] text-white text-xs font-bold tracking-wider px-4 py-1.5 rounded">
                    REFUND &amp; RESCHEDULE POLICY
                  </div>
                </div>
                <ul className="list-disc pl-5 space-y-3">
                  <li>All bookings are <span className="text-red-600 font-semibold">non-refundable</span> once confirmed.</li>
                  <li>Rescheduling requests shall be <span className="font-semibold">subject to availability</span> and management approval.</li>
                  <li><span className="text-red-600 font-semibold">No refund or compensation</span> shall be payable for late arrival or early departure by guests.</li>
                </ul>

                <div className="mt-5 text-center text-[#0a1a3f] font-semibold text-sm">
                  By proceeding with the booking, you agree to abide by the above Terms &amp; Conditions.
                </div>
                <div className="text-center text-[#0a1a3f] font-bold text-sm tracking-wide">
                  ✦ THANK YOU FOR CHOOSING CINEVIBE! ✦
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 px-6 py-4 bg-white">
                <label className="flex items-center gap-2 text-sm text-[#1a1a1a] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-4 h-4 accent-[#0a1a3f]"
                    data-testid="terms-checkbox"
                  />
                  <span>
                    I agree to the Terms & Conditions of{" "}
                    <span className="text-[#0a1a3f] font-semibold">TheCineVibe</span>.
                  </span>
                </label>
                <button
                  type="button"
                  disabled={!termsAccepted || loading}
                  onClick={() => { setShowTerms(false); submit(); }}
                  className="mt-4 w-full py-3 rounded-md font-bold tracking-wider text-white bg-[#1ea84a] hover:bg-[#19913f] disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                  data-testid="terms-continue"
                >
                  CONTINUE
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
          
const SummaryRow = ({ label, value, muted }) => (
  <div className="flex items-center justify-between text-sm py-1">
    <div className="text-[#a89878]">{label}</div>
    <div className={muted ? "text-[#bfb393]" : "text-white"}>{value || "—"}</div>
  </div>
);
